import { AxisAlignedBoundingBox, BoundingRectangle, Math as CMath, Cartesian2, Cartesian3, Color, HeadingPitchRange, Ray, createGuid, PolylineCollection, } from "cesium";
import { HeightMap } from "../../core/height-map";
import { ZBoundedPlane } from "../../core/z-bounded-plane";
import { AssetType, CESIUM_PICKABLE_ENTITY, EmbankmentType, Side, } from "../../types";
import { OBJECT_CELL_SIZE, getDefaultTrenchSettings, } from "../../settings/earthbrain-site-settings";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { Site } from "../site";
import { Activity, ActivityLogger } from "../../utilities/activity-logger";
import { intersectGeometryByPlane } from "../../utilities/geometry-utility";
import { getRaycastableFromRubbingDestination } from "../../utilities/structure-utilities";
import { PointType, } from "../arbitrary-line/types";
import { BendType } from "../mid-path/interface";
import { DEFAULT_TERRAIN_PROFILE_VIEW_STEP_SIZE, DEFAULT_TERRAIN_SECTION_VIEW_STEP_SIZE, } from "./defaults";
import { TrenchGeometry } from "./trench-geometry";
import { ELongitudinalSlopeType, ERubbingDestination, } from "./types";
import { Alignment } from "../../core/alignment/alignment";
import { CesiumRenderableAlignment } from "../alignment/cesium-renderable-alignment";
import { toSectionMarkers } from "../alignment/alignment-section-marker-adapter";
export var ConstructionState;
(function (ConstructionState) {
    ConstructionState["UNDER_CONSTRUCTION"] = "under-construction";
    ConstructionState["UNDER_EDIT"] = "under-edit";
    ConstructionState["READY"] = "ready";
    ConstructionState["NONE"] = "none";
})(ConstructionState || (ConstructionState = {}));
export class Trench {
    assetType = AssetType.Trench_Data;
    _verticalAlignmentData;
    _wgs84dataMemo;
    _id;
    _constructionState;
    _polylineCollection;
    _trenchSettings;
    _trenchGeometry;
    _viewer;
    _2dDataMemo;
    _isMagnified = false;
    _alignment;
    _cesiumRenderableAlignment;
    //RENDER Entities
    _renderData;
    get geometry() {
        return this._trenchGeometry.geometry;
    }
    get isMagnified() {
        return this._isMagnified;
    }
    set isMagnified(_val) {
        this._isMagnified = _val;
    }
    _associatedTerrain;
    _associatedRubbingDestination;
    _debugEntities = [];
    _heightMap;
    // Visibility intent (source of truth). Primitives' `.show` mirror this.
    _isVisible = true;
    get isVisible() {
        return this._isVisible;
    }
    get referencePointElevation() {
        if (this._trenchSettings.commonSetting.path.referencePointElevation ===
            undefined) {
            this._trenchSettings.commonSetting.path.referencePointElevation =
                +this._alignment.ipLinePoints[0].localPoint.z.toFixed(3);
        }
        return +this._trenchSettings.commonSetting.path.referencePointElevation.toFixed(3);
    }
    get averageHeightOfFirstAndLastPoint() {
        const heightOfStartPoint = this._alignment.ipLinePoints[0].localPoint.z;
        const heightOfLastPoint = this._alignment.ipLinePoints[this._alignment.ipLinePoints.length - 1]
            .localPoint.z;
        return (heightOfStartPoint + heightOfLastPoint) * 0.5;
    }
    constructor(viewer, id, trenchSettings, associatedTerrain, associatedRubbingDestination) {
        this._id = id;
        this._trenchSettings = trenchSettings || getDefaultTrenchSettings();
        this._associatedTerrain = associatedTerrain;
        this._associatedRubbingDestination = associatedRubbingDestination;
        this._viewer = viewer;
        this._trenchGeometry = new TrenchGeometry(this._id);
        this._alignment = new Alignment(this._id);
        this._polylineCollection = new PolylineCollection();
        viewer.scene.primitives.add(this._polylineCollection);
        this._2dDataMemo = {
            altitudeViewData: {
                designDataOutlines: [],
                flatGroundDataOutlines: [],
                pathPoints: [],
                pointCloudOutlines: [],
                referenceTopologyOutlines: [],
                sectionLineDistFromFirstPoint: 0,
                slopeBreaks: [],
                sectionMarkers: [],
            },
            sectionViewData: {
                designDataOutlines: [],
                flatGroundDataOutlines: [],
                pathPoints: [],
                pointCloudOutlines: [],
                referenceTopologyOutlines: [],
                referencePointIndex: 0,
            },
            distanceFromStart: 0,
        };
        this._renderData = {
            geometryPrimitives: [],
        };
        this._cesiumRenderableAlignment = new CesiumRenderableAlignment(this._alignment, this._viewer, {
            width: this._trenchSettings.commonSetting.path.controlLineSettings.width,
            color: this._trenchSettings.commonSetting.path.controlLineSettings.color,
            outlineWidth: this._trenchSettings.commonSetting.path.controlLineSettings
                .outlineWidth,
            outlineColor: this._trenchSettings.commonSetting.path.controlLineSettings
                .outlineColor,
        });
        this._constructionState = ConstructionState.NONE;
        this._wgs84dataMemo = { geometries: [] };
    }
    set constructionState(state) {
        this._constructionState = state;
    }
    get constructionState() {
        return this._constructionState;
    }
    updateAssociatedTerrain(terrain) {
        this._associatedTerrain = terrain;
    }
    async updateAssociatedRubbingDestination(rubbingDestination) {
        this._associatedRubbingDestination = rubbingDestination;
        await this.updateGeometry();
    }
    static async FromControlPoints(controlPointArray, trenchSetting, cesiumViewer, associatedTerrain, associatedRubbingDestination) {
        const trench = new Trench(cesiumViewer.viewer, createGuid(), trenchSetting, associatedTerrain, associatedRubbingDestination);
        const controlPoints = [];
        for (let iter = 0; iter < controlPointArray.length; iter += 3) {
            controlPoints.push({
                point: new Cartesian3(controlPointArray[iter], controlPointArray[iter + 1], controlPointArray[iter + 2]),
                bendType: BendType.NONE,
            });
        }
        trench._constructionState = ConstructionState.READY;
        return trench;
    }
    async initializeTrenchFromAlignmentData(surveyPoints, ipPoints, horizontalAlignmentData, verticalAlignmentData, stationInterval) {
        const ipPointIds = await this._cesiumRenderableAlignment.initializeFromAlignmentData(surveyPoints, ipPoints, horizontalAlignmentData, verticalAlignmentData, stationInterval);
        await this.updateGeometry();
        return ipPointIds;
    }
    static async fromJSON(trenchData, viewer, stationInterval) {
        const site = Site.getInstance();
        if (!trenchData) {
            throw new Error("Invalid trench data");
        }
        const associatedTerrain = site.getTerrain(trenchData.associatedTerrain.id, trenchData.associatedTerrain.terrainType);
        if (!associatedTerrain) {
            throw new Error("Invalid associated terrain");
        }
        const trenchUnderConstruction = new Trench(viewer.viewer, trenchData.trenchId, trenchData.trenchSettings, associatedTerrain, trenchData.rubbingDestination);
        trenchUnderConstruction.id = trenchData.trenchId;
        await trenchUnderConstruction.initializeTrenchFromAlignmentData(trenchData.surveyPoints, trenchData.ipPointIds, trenchData.horizontalAlignment, trenchData.verticalAlignment, stationInterval);
        return trenchUnderConstruction;
    }
    get associatedTerrain() {
        return this._associatedTerrain;
    }
    get associatedRubbingDestination() {
        return this._associatedRubbingDestination;
    }
    get controlPointLength() {
        return this._alignment.ipLinePoints.length;
    }
    get id() {
        return this._id;
    }
    set id(newID) {
        this._id = newID;
    }
    get settings() {
        return this._trenchSettings;
    }
    get trenchSegments() {
        return [];
    }
    get heightMap() {
        return this._heightMap;
    }
    get alignment() {
        return this._alignment;
    }
    get cesiumRenderableAlignment() {
        return this._cesiumRenderableAlignment;
    }
    /**
     * Is control point removable
     * @param controlPointId Id of the control point
     * @returns Returns true if the control point is removable and in all other cases returns false
     */
    /**
     * Is control point removable
     * @param controlPointId Id of the control point
     * @returns Returns true if the control point is removable and in all other cases returns false
     */
    isControlPointRemovable(controlPointId) {
        return (this._alignment.ipLinePoints[0].id !== controlPointId &&
            this._alignment.ipLinePoints[this._alignment.ipLinePoints.length - 1]
                .id !== controlPointId);
    }
    getPointIDFromIndex(index) {
        return this._alignment.ipLinePoints[index].id;
    }
    async _updateWGS84Data(trenchSegGeometries) {
        const site = Site.getInstance();
        // Batch all segment vertices into a single convertPointsToWGS84 call
        // to minimize Worker WebSocket queue round-trips
        const segmentVerticesArrays = trenchSegGeometries.map((data) => Cartesian3.unpackArray(data.geometry.vertices));
        const allPoints = [];
        const segmentOffsets = [];
        for (const vertices of segmentVerticesArrays) {
            segmentOffsets.push({ start: allPoints.length, count: vertices.length });
            allPoints.push(...vertices);
        }
        try {
            const allConverted = await site.convertPointsToWGS84(allPoints);
            if (allConverted) {
                for (let i = 0; i < trenchSegGeometries.length; i++) {
                    const { start, count } = segmentOffsets[i];
                    trenchSegGeometries[i].geometry.vertices = Cartesian3.packArray(allConverted.slice(start, start + count));
                }
            }
        }
        catch (error) {
            console.warn(error);
            for (const data of trenchSegGeometries) {
                data.geometry.vertices.length = 0;
            }
        }
        this._wgs84dataMemo.geometries = trenchSegGeometries;
    }
    async updateGeometry() {
        // Control point count may have changed (e.g. IP added/removed); refresh vertical alignment candidates.
        this._updateLongitudinalSlopeBreaks();
        this._alignment.updateVerticalAlignmentData(this._verticalAlignmentData);
        await this.recomputeGeometry();
        await this.render();
    }
    getSectionOrientationAt3dDistance(distanceFromStart) {
        return this._alignment.getSectionOrientationAt3dDistance(distanceFromStart);
    }
    getCrossSectionPathPoints(distanceFromStart) {
        const data = this._getTrenchCrossSectionViewData(Cartesian3.ZERO, Cartesian3.ZERO, distanceFromStart);
        return [
            ...data.pathPoints,
            ...data.pointCloudOutlines.flat(),
            ...data.designDataOutlines.filter(Boolean).flat(),
            ...data.referenceTopologyOutlines.filter(Boolean).flat(),
            ...(data.flatGroundDataOutlines ?? []).flat(),
        ];
    }
    getSegmentAtDistanceFromStartPoint(dist, thickness) {
        const pointsToExtrudeSectionPlane = [];
        // let remainingDistToTravel = 0;
        const { pointOnPath, bounds } = this._alignment.getPointInfoAt3dDistance(dist);
        if (!pointOnPath) {
            return pointsToExtrudeSectionPlane;
        }
        //calculate the unit direction for this segment
        const direction = EarthBrainUtils.getNormalizedDirection(bounds[0], bounds[1]);
        //use linear interpolation on this segment to get the finalPrevPoint.
        // const finalPrevPoint = EarthBrainUtils.getDistantCartesian(prevPoint, direction, remainingDistToTravel);
        // find the next point at say remainingDistToTravel + 1, to get width to the entity
        const finalNextPoint = EarthBrainUtils.getDistantCartesian(pointOnPath, direction, thickness);
        pointsToExtrudeSectionPlane.push(pointOnPath, finalNextPoint);
        return pointsToExtrudeSectionPlane;
    }
    pickEntity(entity) {
        const result = { type: undefined, entity: undefined };
        const data = this._alignment.ipLinePoints.find((p) => p.id === entity.id);
        if (data) {
            result.type = CESIUM_PICKABLE_ENTITY.TRENCH_CONTROL_POINT;
            result.entity = this._cesiumRenderableAlignment.getIPSphereEntity(data.id);
        }
        return result;
    }
    /**
     * Adds point a the end
     * @param pointInfo
     * @returns
     */
    async addIPPointAtEnd(pointInfo) {
        return await this.addIPPointAtIndex(this._alignment.ipLinePoints.length, {
            wgs84Position: pointInfo.position,
            localPosition: pointInfo.localPosition,
        });
    }
    /**
     * This function will calculate MidControlPoint and will update this point in controlPointBufferMap at particular index
     * @param controlPointID ControlPointID on which this function is called
     * @param viewer Cesium Viewer
     * @returns Newly added control point ID
     */
    async addIPPointOnTrench(ipPointId) {
        // Calculating midpoint between the current controlPoint and previous or post control point depending on the situation.
        const index = this._alignment.ipLinePoints.findIndex((ipPoint) => ipPoint.id === ipPointId);
        if (index === -1) {
            return;
        }
        const currentPoint = this._cesiumRenderableAlignment.ipLinePoints[index].wgs84Point;
        const neighborPoint = index === 0
            ? this._cesiumRenderableAlignment.ipLinePoints[index + 1].wgs84Point
            : this._cesiumRenderableAlignment.ipLinePoints[index - 1].wgs84Point;
        const newMidPoint = Cartesian3.midpoint(currentPoint, neighborPoint, new Cartesian3());
        return await this.addIPPointAtIndex(index === 0 ? index + 1 : index, {
            wgs84Position: newMidPoint,
        });
    }
    /**
     * This function will add control point at index will also update controlPointBufferMap and _updateTrenchGeometry()
     * @param index INDEX at which this function point will get added
     * @param localPos POSITION of control point in local space
     * @param commandInputs Command Inputs
     */
    async addIPPointAtIndex(index, pointInfo) {
        const ipPointId = await this._cesiumRenderableAlignment.addIPPointAtIndex(index, pointInfo);
        await this._afterIPPointAdded();
        return ipPointId;
    }
    /**
     * Undo-only: re-insert a deleted IP point with its original ID.
     * Only for a deleted middle IP point; at first/last index `ipId` is ignored and a new ID is issued.
     */
    async restoreControlPointAtIndex(index, localPosition, ipId) {
        await this._cesiumRenderableAlignment.addIPPointAtIndex(index, { localPosition }, ipId);
        await this._afterIPPointAdded();
    }
    /** Post-processing after an IP point is inserted: render or recompute geometry by construction state, then log. */
    async _afterIPPointAdded() {
        if (this._constructionState === ConstructionState.UNDER_CONSTRUCTION) {
            await this._cesiumRenderableAlignment.render();
        }
        else {
            await this.updateGeometry();
        }
        ActivityLogger.log(Activity.TRENCH_CONTROLPOINT_UPDATED);
    }
    async updateControlPointPosition(pointInfo, clickedControlPointID) {
        const ipPointData = this._alignment.ipLinePoints.find((p) => p.id === clickedControlPointID);
        if (!ipPointData) {
            throw new Error("Control point not found");
        }
        const oldPointInfo = {
            position: null, //ipPointData.localPoint.clone(),
            info: null, //ipPointData.pointInfo,
            localPosition: ipPointData.localPoint.clone(),
        };
        await this._cesiumRenderableAlignment.updateIPPointPosition(pointInfo, clickedControlPointID);
        await this.updateGeometry();
        ActivityLogger.log(Activity.TRENCH_CONTROLPOINT_UPDATED);
        return oldPointInfo;
    }
    getIndexOfPointOnIPPath(point) {
        return this._alignment.getIndexOfPointOnIPPath(point);
    }
    getControlPointIndex(id) {
        return this._alignment.ipLinePoints.findIndex((p) => p.id === id);
    }
    getControlPointPos(id) {
        const ipPointInfo = this._alignment.ipLinePoints.find((p) => p.id === id);
        return ipPointInfo?.localPoint.clone();
    }
    async removeControlPoint(clickedControlPointID) {
        await this._cesiumRenderableAlignment.removeIPPoint(clickedControlPointID);
        if (this._constructionState === ConstructionState.UNDER_CONSTRUCTION) {
            await this._cesiumRenderableAlignment.render();
        }
        else {
            await this.updateGeometry();
        }
        ActivityLogger.log(Activity.TRENCH_CONTROLPOINT_UPDATED);
    }
    getCorrespondingProjectedDistFromStart(distanceFromStart) {
        return this._alignment.getCorrProjectedDistanceFromStartPoint(distanceFromStart);
    }
    getCorrespondingDistanceFromStart(projectedDistanceFromStart) {
        return this._alignment.getCorrDistanceFromStartPoint(projectedDistanceFromStart);
    }
    getClosestPointInfoOnPath(point) {
        return this._alignment.getClosestPointInfoOnPath(point);
    }
    toJSON() {
        try {
            const { horizontalAlignment, verticalAlignment, surveyPoints, ipPointIds, } = this._cesiumRenderableAlignment.toJSON();
            const trenchInfo = {
                trenchId: this._id,
                trenchSettings: this._trenchSettings,
                associatedTerrain: {
                    id: this._associatedTerrain.id,
                    terrainType: this._associatedTerrain.assetType,
                },
                rubbingDestination: this._associatedRubbingDestination,
                surveyPoints,
                ipPointIds,
                horizontalAlignment: {
                    type: this._alignment.horizontalAlignment.type,
                    data: horizontalAlignment,
                },
                verticalAlignment: verticalAlignment,
            };
            return trenchInfo;
        }
        catch (error) {
            console.log(error);
        }
    }
    getTrenchSegments() {
        return []; //this._trenchGeometry.segments;
    }
    // ToDo: Should be a property
    // ToDo: Return value should be cached
    getLength() {
        return this._alignment.length;
    }
    getHeightInfo() {
        return this._alignment.verticalAlignment.getHeightRange();
    }
    getSlopeInfo() {
        return this._alignment.verticalAlignment.getSlopeRange();
    }
    get2DViewerData({ distanceFromStart = 0, }) {
        const site = Site.getInstance();
        let updatePointCloud = false;
        let updateDesignData = false;
        let updateCrossSection = false;
        let updateFlatGroundData = false;
        let updateReferenceTopology = false;
        let updateAll = false;
        let activity = ActivityLogger.latest;
        while (activity) {
            let breakWhile = false;
            switch (activity.activity) {
                case Activity.TRENCH_2D_DATA_CALCULATED:
                    breakWhile = true;
                    break;
                //pointcloud-outlines,
                case Activity.POINTCLOUD_LOADED:
                case Activity.POINT_CLOUD_VISIBILITY_UPDATE:
                    updatePointCloud = true;
                    break;
                //designdata-outlines
                case Activity.DESIGNDATA_LOADED:
                case Activity.DESIGNDATA_VISIBILITY_UPDATE:
                    updateDesignData = true;
                    break;
                case Activity.REFERENCE_TOPOLOGY_LOADED:
                case Activity.REFERENCE_TOPOLOGY_VISIBILITY_UPDATE:
                case Activity.REFERENCE_TOPOLOGY_EDITED:
                    updateReferenceTopology = true;
                    break;
                case Activity.TRENCH_LOADED: //all
                case Activity.TRENCH_EDIT_STARTED: //all
                case Activity.TRENCH_CONTROLPOINT_UPDATED: // all
                case Activity.TRENCH_GEOMETRYSHAPE_UPDATED: // all
                    updateAll = true;
                    breakWhile = true;
                    break;
                case Activity.TRENCH_SETTINGS_UPDATED: // cross-section
                case Activity.FLATGROUNDDATA_LOADED:
                case Activity.FLATGROUNDDATA_VISIBILITY_UPDATE:
                    updateCrossSection = true;
                    updateFlatGroundData = true;
                    break;
                case Activity.TRENCH_SLOPEBREAK_UPDATED: // cross-section
                case Activity.TRENCH_SECTION_PLANE_UPDATED: // cross-section
                    updateCrossSection = true;
                    break;
            }
            if (breakWhile)
                break;
            activity = activity.previous;
        }
        if (this._2dDataMemo.distanceFromStart !== distanceFromStart) {
            updateCrossSection = true;
            this._2dDataMemo.distanceFromStart = distanceFromStart;
        }
        this._2dDataMemo.altitudeViewData.slopeBreaks =
            this._verticalAlignmentData.map((va) => ({
                percentage: va.horizontalDistance / this._alignment.lengthOnDatum,
                height: va.elevation,
                isActive: false,
                associatedControlPointId: va.associatedIPPointId,
            }));
        const trenchOutlineCartesians = this._traceTrenchOnPointCloud(DEFAULT_TERRAIN_PROFILE_VIEW_STEP_SIZE);
        const updatePathLineData = () => {
            this._2dDataMemo.altitudeViewData.pathPoints =
                this._createOutlineForTrench();
            this._2dDataMemo.altitudeViewData.sectionMarkers = toSectionMarkers(this._alignment.centerLinePoints);
        };
        const updateCrossSectionRelatedData = () => {
            this._2dDataMemo.altitudeViewData.sectionLineDistFromFirstPoint =
                this._2dDataMemo.altitudeViewData.pathPoints[0][0] +
                    this.getCorrespondingProjectedDistFromStart(distanceFromStart);
            const positionsToExtrudeSectionPlane = this.getSegmentAtDistanceFromStartPoint(distanceFromStart, 0.1);
            const sectionViewData = this._getTrenchCrossSectionViewData(positionsToExtrudeSectionPlane[0], positionsToExtrudeSectionPlane[1], this._2dDataMemo.altitudeViewData.sectionLineDistFromFirstPoint);
            this._2dDataMemo.sectionViewData.pathPoints = sectionViewData.pathPoints;
            this._2dDataMemo.sectionViewData.pointCloudOutlines =
                sectionViewData.pointCloudOutlines;
            this._2dDataMemo.sectionViewData.designDataOutlines =
                sectionViewData.designDataOutlines;
            this._2dDataMemo.sectionViewData.flatGroundDataOutlines =
                sectionViewData.flatGroundDataOutlines;
            this._2dDataMemo.sectionViewData.referencePointIndex =
                sectionViewData.referencePointIndex;
            this._2dDataMemo.sectionViewData.referenceTopologyOutlines =
                sectionViewData.referenceTopologyOutlines;
        };
        const updatePointCloudRelatedData = () => {
            this._2dDataMemo.altitudeViewData.pathPoints =
                this._createOutlineForTrench();
            this._2dDataMemo.altitudeViewData.sectionLineDistFromFirstPoint =
                this._2dDataMemo.altitudeViewData.pathPoints[0][0] +
                    this.getCorrespondingProjectedDistFromStart(distanceFromStart);
            this._2dDataMemo.altitudeViewData.slopeBreaks =
                this._verticalAlignmentData.map((va) => ({
                    percentage: va.horizontalDistance / this._alignment.lengthOnDatum,
                    height: va.elevation,
                    isActive: false,
                    associatedControlPointId: va.associatedIPPointId,
                }));
            const trench_pointCloudOutlines = [];
            site.getPointClouds().forEach((data) => {
                if (data.tileSet.show) {
                    const { outline } = this._createOutlineForTileset(trenchOutlineCartesians, data);
                    trench_pointCloudOutlines.push(outline);
                }
                this._2dDataMemo.altitudeViewData.pointCloudOutlines =
                    trench_pointCloudOutlines;
            });
        };
        const updateDesignDataRelatedData = () => {
            const trench_designDataOutlines = [];
            site.getDesignDatas().forEach((data) => {
                if (data.tileSet.show) {
                    const { outline } = this._createOutlineForTileset(trenchOutlineCartesians, data);
                    trench_designDataOutlines.push(outline);
                }
            });
            this._2dDataMemo.altitudeViewData.designDataOutlines =
                trench_designDataOutlines;
        };
        const updateReferenceTopologyRelatedData = () => {
            const trench_referenceTopologyOutlines = [];
            site.getReferenceTopologies().forEach((data) => {
                if (data.getDrawing2DImage().isVisible()) {
                    const { outline } = this._createOutlineForTileset(trenchOutlineCartesians, data);
                    trench_referenceTopologyOutlines.push(outline);
                }
            });
            this._2dDataMemo.altitudeViewData.referenceTopologyOutlines =
                trench_referenceTopologyOutlines;
        };
        const updateFlatGroundRelatedData = () => {
            const trenchFlatGroundDataOutlines = [];
            if (this._associatedRubbingDestination.target ===
                ERubbingDestination.FLAT_GROUND) {
                const flatGround = site.getFlatGround(this._associatedRubbingDestination.id);
                if (flatGround?.isVisible()) {
                    const { outline } = this._createOutlineForRubbingDestinationFlatGround(trenchOutlineCartesians, flatGround);
                    trenchFlatGroundDataOutlines.push(outline);
                }
            }
            this._2dDataMemo.altitudeViewData.flatGroundDataOutlines =
                trenchFlatGroundDataOutlines;
        };
        updatePathLineData();
        if (updateAll) {
            updatePointCloudRelatedData();
            updateDesignDataRelatedData();
            updateFlatGroundRelatedData();
            updateCrossSectionRelatedData();
            updateReferenceTopologyRelatedData();
        }
        else if (updatePointCloud) {
            updatePointCloudRelatedData();
            updateCrossSectionRelatedData();
        }
        else if (updateDesignData) {
            updateDesignDataRelatedData();
            updateCrossSectionRelatedData();
        }
        else if (updateFlatGroundData) {
            updateFlatGroundRelatedData();
            updateCrossSectionRelatedData();
        }
        else if (updateReferenceTopology) {
            updateReferenceTopologyRelatedData();
            updateCrossSectionRelatedData();
        }
        else if (updateCrossSection) {
            updateCrossSectionRelatedData();
        }
        ActivityLogger.log(Activity.TRENCH_2D_DATA_CALCULATED);
        return this._2dDataMemo;
    }
    _getTrenchCrossSectionViewData(startPointOfSectionPlane, endPointOfSectionPlane, distanceFromStartPoint) {
        const result = {
            pathPoints: [],
            pointCloudOutlines: [],
            designDataOutlines: [],
            flatGroundDataOutlines: [],
            referenceTopologyOutlines: [],
            referencePointIndex: -1,
        };
        const site = Site.getInstance();
        const orientation = this.getSectionOrientationAt3dDistance(distanceFromStartPoint);
        if (!orientation) {
            return result;
        }
        const { pointOnPath, direction } = orientation;
        const { left, right } = this._trenchGeometry.pathGeometry.getSidePoints(pointOnPath, direction);
        const sortBy2DDistanceFromStartPoint = (points, start) => {
            points.sort((a, b) => Cartesian2.distance(start, a) - Cartesian2.distance(start, b)); // Assending Order
        };
        const zBoundedPlane = ZBoundedPlane.fromPointNormal(pointOnPath, 
        // ZBoundedPlane requires a normal parallel to the xy plane
        Cartesian3.normalize(new Cartesian3(direction.forward.x, direction.forward.y, 0), new Cartesian3())); // no bounds as we need whole of road
        const intersectedPoints = intersectGeometryByPlane(this._trenchGeometry.geometry, zBoundedPlane);
        const leftFacingPlane = ZBoundedPlane.fromPointNormal(left, direction.left);
        const rightFacingPlane = ZBoundedPlane.fromPointNormal(right, direction.right);
        const leftPoints = intersectedPoints.filter((p) => leftFacingPlane.raycast(new Ray(p, direction.right)));
        const rightPoints = intersectedPoints.filter((p) => rightFacingPlane.raycast(new Ray(p, direction.left)));
        sortBy2DDistanceFromStartPoint(leftPoints, pointOnPath);
        sortBy2DDistanceFromStartPoint(rightPoints, pointOnPath);
        leftPoints.unshift(left);
        rightPoints.unshift(right);
        const filteredLeftPoints = [];
        for (const point of leftPoints) {
            if (!filteredLeftPoints[filteredLeftPoints.length - 1] ||
                Cartesian3.distance(filteredLeftPoints[filteredLeftPoints.length - 1], point) > 0.00000001) {
                filteredLeftPoints.push(point);
            }
        }
        const filteredRightPoints = [];
        for (const point of rightPoints) {
            if (!filteredRightPoints[filteredRightPoints.length - 1] ||
                Cartesian3.distance(filteredRightPoints[filteredRightPoints.length - 1], point) > 0.00000001) {
                filteredRightPoints.push(point);
            }
        }
        filteredLeftPoints.reverse();
        const points3d = [
            ...filteredLeftPoints,
            pointOnPath,
            ...filteredRightPoints,
        ];
        const points2d = [];
        for (const point of points3d) {
            const translatedPoint = Cartesian3.subtract(point, pointOnPath, new Cartesian3());
            const dist = Cartesian3.dot(direction.right, translatedPoint);
            points2d.push([dist, point.z]);
        }
        const cesium2List = points2d.map((p) => Cartesian2.fromArray(p));
        const box = BoundingRectangle.fromPoints(cesium2List);
        // We should be considering 1.5 times the bottom trench width in each direction
        // meaning 3 times the bottom width of the trench
        const terrianLineRegion = 2 * box.width;
        // Step is defined as 0.5 meters
        const step = DEFAULT_TERRAIN_SECTION_VIEW_STEP_SIZE;
        const pointCountOnEachSide = Math.floor(terrianLineRegion / (2 * step));
        const trenchOutlineCartesians = [];
        trenchOutlineCartesians.push(pointOnPath);
        let i = 1;
        while (i < pointCountOnEachSide) {
            const pLeft = new Cartesian3();
            Cartesian3.multiplyByScalar(direction.left, i * step, pLeft);
            Cartesian3.add(pLeft, pointOnPath, pLeft);
            trenchOutlineCartesians.unshift(pLeft);
            const pRight = new Cartesian3();
            Cartesian3.multiplyByScalar(direction.right, i * step, pRight);
            Cartesian3.add(pRight, pointOnPath, pRight);
            trenchOutlineCartesians.push(pRight);
            ++i;
        }
        // The 2D projected coordinates for the trench are referenced to the center of the trench
        // Since the outlines are referenced to the leftmost point we need to align them with the center
        // Hence translating in x by center point on the outline
        const prepareOutline = (points, tileset) => {
            const { offsetToCenter, outline } = this._createOutlineForTileset(points.map((p) => ({
                pathPoint: p,
                pathPointOnDatum: new Cartesian3(p.x, p.y, 0),
            })), tileset);
            for (const point of outline) {
                point[0] = point[0] - offsetToCenter;
            }
            return outline;
        };
        const prepareOutlineForFlatGround = (points, targetId) => {
            const flatGround = site.getFlatGround(targetId);
            if (flatGround?.isVisible()) {
                const { offsetToCenter, outline } = this._createOutlineForRubbingDestinationFlatGround(points.map((p) => ({
                    pathPoint: p,
                    pathPointOnDatum: new Cartesian3(p.x, p.y, 0),
                })), site.getFlatGround(targetId));
                for (const point of outline) {
                    point[0] = point[0] - offsetToCenter;
                }
                return outline;
            }
            else {
                return [];
            }
        };
        site.getPointClouds().forEach((data) => {
            if (data.tileSet.show) {
                result.pointCloudOutlines.push(prepareOutline(trenchOutlineCartesians, data));
            }
        });
        result.designDataOutlines = site.getDesignDatas().map((d) => {
            if (d.tileSet.show) {
                return prepareOutline(trenchOutlineCartesians, d);
            }
        });
        site.getReferenceTopologies().forEach((data) => {
            if (data.getDrawing2DImage().isVisible()) {
                result.referenceTopologyOutlines.push(prepareOutline(trenchOutlineCartesians, data));
            }
        });
        result.flatGroundDataOutlines =
            this._associatedRubbingDestination.target ===
                ERubbingDestination.FLAT_GROUND
                ? [
                    prepareOutlineForFlatGround(trenchOutlineCartesians, this._associatedRubbingDestination.id),
                ]
                : [];
        // This point from trench points array will be used to calculate the measurements for the trench
        result.pathPoints = points2d;
        result.referencePointIndex = result.pathPoints.findIndex((p) => p[0] === 0);
        return result;
    }
    /**
     * Creates a list of points as outline for 2D view for a tileset.
     * Plots distance between points on X
     * Plots the point height / terrain height on Y
     * @param viewer Viewer
     * @param pathArray Points of the tileset
     * @param tileset tileset on which points need to be plotted
     * @returns
     */
    _createOutlineForTileset(pathArray, tileset) {
        const centerIndex = Math.floor(pathArray.length / 2);
        let offsetToCenter = 0;
        const outline = [];
        let xDistFromStart = 0;
        for (let i = 0; i < pathArray.length; ++i) {
            if (pathArray[i - 1]) {
                xDistFromStart += Cartesian3.distance(pathArray[i - 1].pathPointOnDatum, pathArray[i].pathPointOnDatum);
            }
            if (i === centerIndex) {
                offsetToCenter = xDistFromStart;
            }
            const projectHeight = tileset.getProjectPointHeight(pathArray[i].pathPoint);
            if (projectHeight) {
                outline.push([xDistFromStart, projectHeight]);
            }
        }
        return { offsetToCenter, outline };
    }
    /**
     * Creates a list of points as outline for 2D view for a trench.
     * @returns number[][]
     */
    _createOutlineForTrench() {
        const trenchPoints = this._alignment.centerLinePoints;
        const outline = [];
        let dist = 0;
        let cartDistance = 0;
        for (let i = 0; i < trenchPoints.length; ++i) {
            if (trenchPoints[i - 1]) {
                dist += Cartesian2.distance(trenchPoints[i - 1].localPointWithAdjustedHeight, trenchPoints[i].localPointWithAdjustedHeight);
                cartDistance += Cartesian3.distance(trenchPoints[i - 1].localPointWithAdjustedHeight, trenchPoints[i].localPointWithAdjustedHeight);
            }
            const height = trenchPoints[i].localPointWithAdjustedHeight.z; // Offsetting is taken care in altitude & section viewer classes
            outline.push([dist, height, cartDistance]);
        }
        return outline;
    }
    _tracePointCloudBetweenTwoPoints(startPoint, endPoint, step, includeStart, includeEnd) {
        const result = [];
        if (Cartesian3.equals(startPoint, endPoint)) {
            console.warn(`Start and End point for are same. Please check the data for trench ${this.id}.`);
        }
        else {
            const dir = EarthBrainUtils.getNormalizedDirection(startPoint, endPoint);
            const distBetweenPoint = Cartesian3.distance(startPoint, endPoint);
            const count = Math.floor(distBetweenPoint / step);
            let i = 0;
            includeStart && result.push(startPoint);
            while (i < count) {
                const pointAtStep = new Cartesian3();
                Cartesian3.multiplyByScalar(dir, i * step, pointAtStep);
                Cartesian3.add(pointAtStep, startPoint, pointAtStep);
                result.push(pointAtStep);
                i++;
            }
            includeEnd && result.push(endPoint);
        }
        return result;
    }
    _traceTrenchOnPointCloud(stepBetweenTwoPoints) {
        const pathPoints = this._alignment.centerLinePoints;
        const trenchOutlineCartesians = [];
        for (let j = 0; j < pathPoints.length - 1; j++) {
            const startPoint = pathPoints[j].localPointWithAdjustedHeight;
            const endPoint = pathPoints[j + 1].localPointWithAdjustedHeight;
            const isLastPoint = j + 1 === pathPoints.length - 1;
            const points = this._tracePointCloudBetweenTwoPoints(startPoint, endPoint, stepBetweenTwoPoints, true, isLastPoint);
            if (trenchOutlineCartesians[trenchOutlineCartesians.length - 1] &&
                points[0]) {
                if (Cartesian3.equals(trenchOutlineCartesians[trenchOutlineCartesians.length - 1]
                    .pathPoint, points[0])) {
                    trenchOutlineCartesians.pop();
                }
            }
            trenchOutlineCartesians.push(...points.map((p) => ({
                pathPoint: p,
                pathPointOnDatum: new Cartesian3(p.x, p.y, 0),
            })));
        }
        return trenchOutlineCartesians;
    }
    /**
     * Create outline of flat ground used as a rubbing destination
     * @param pathArray Object Array containing path points & path points on datum
     * @param flatGround FlatGround rubbing destination
     * @returns { offsetToCenter: number; outline: number[][] }
     */
    _createOutlineForRubbingDestinationFlatGround(pathArray, flatGround) {
        const centerIndex = Math.floor(pathArray.length / 2);
        let offsetToCenter = 0;
        const outline = [];
        let xDistFromStart = 0;
        for (let i = 0; i < pathArray.length; ++i) {
            if (i !== 0) {
                xDistFromStart += Cartesian3.distance(pathArray[i - 1].pathPointOnDatum, pathArray[i].pathPointOnDatum);
            }
            if (i === centerIndex) {
                offsetToCenter = xDistFromStart;
            }
            const projectHeight = flatGround.getProjectPointHeight(pathArray[i].pathPoint);
            if (projectHeight) {
                outline.push([xDistFromStart, projectHeight]);
            }
        }
        return { offsetToCenter, outline };
    }
    getSegmentByPoint(point) {
        return null; //this._trenchGeometry.getClosestPointInfoOnPath(point)?.segment;
    }
    _getColorBySegmentSide(side) {
        switch (side) {
            case Side.CENTER:
                const trenchSurfaceMaterial = Color.fromCssColorString(this._trenchSettings.commonSetting.path.color); //Color.fromRandom().withAlpha(1.0);
                trenchSurfaceMaterial.alpha = this._trenchSettings.commonSetting.path
                    .transparency
                    ? this._trenchSettings.commonSetting.path.transparency
                    : trenchSurfaceMaterial.alpha;
                return trenchSurfaceMaterial;
            case Side.LEFT:
            case Side.RIGHT:
                const cutMaterial = Color.fromCssColorString(this._trenchSettings.commonSetting.cutSetting.color);
                cutMaterial.alpha =
                    this._trenchSettings.commonSetting.cutSetting.transparency;
                return cutMaterial;
        }
    }
    _getPickableTypeBySegmentSide(side) {
        switch (side) {
            case Side.CENTER:
                return CESIUM_PICKABLE_ENTITY.TRENCH_SEGMENT;
            case Side.LEFT:
            case Side.RIGHT:
                return CESIUM_PICKABLE_ENTITY.TRENCH_CUT_GEOMETRY;
        }
    }
    // Removes the path from viewer. Clears all the data
    dispose() {
        this._trenchGeometry.dispose();
        this._trenchGeometry = undefined;
        this._id = "";
        this._trenchSettings = undefined;
        this.disposeRenderData();
        this._viewer.scene.primitives.remove(this._polylineCollection);
        if (!this._polylineCollection.isDestroyed()) {
            this._polylineCollection.removeAll();
            this._polylineCollection.destroy();
        }
        this._polylineCollection = undefined;
    }
    async setTypeToHorizontalAlignment(type) {
        if (this._alignment.horizontalAlignment.type === type) {
            return;
        }
        this._alignment.setTypeToHorizontalAlignment(type);
        // IP projection distances change with the curve type; re-anchor the IP slope breaks to them.
        await this.updateGeometry();
    }
    async updatePathSettings(settings, rubbingDestination) {
        this._trenchSettings = settings;
        if (rubbingDestination) {
            this._associatedRubbingDestination = rubbingDestination;
        }
        await this.updateGeometry();
        ActivityLogger.log(Activity.TRENCH_SETTINGS_UPDATED);
    }
    updateMaterial(surfaceColor, surfaceTransparency, cutMaterialColor, cutMaterialTransparency) {
        if (surfaceColor) {
            this._trenchSettings.commonSetting.path.color = surfaceColor;
        }
        if (surfaceTransparency) {
            this._trenchSettings.commonSetting.path.transparency =
                surfaceTransparency;
        }
        if (cutMaterialColor) {
            this._trenchSettings.commonSetting.cutSetting.color = cutMaterialColor;
        }
        if (cutMaterialTransparency) {
            this._trenchSettings.commonSetting.cutSetting.transparency =
                cutMaterialTransparency;
        }
        this.renderGeometry();
    }
    getPathPoints() {
        return this._alignment.centerLinePoints.map((p) => p.localPointWithAdjustedHeight);
    }
    _removeDebugEntities() {
        this._debugEntities.forEach((entity) => {
            this._viewer.entities.remove(entity);
        });
        this._debugEntities = [];
    }
    setAlignmentVisibility(show) {
        this._cesiumRenderableAlignment.setVisibility(show);
    }
    setVisibility(show) {
        this.setIpLineVisibility(show);
        if (!show) {
            this.setCenterLineVisibility(false);
        }
        if (this._renderData.geometryPrimitives) {
            this._renderData.geometryPrimitives.forEach(({ primitive }) => {
                primitive.show = show;
            });
        }
    }
    setIpLineVisibility(visible) {
        this._cesiumRenderableAlignment.setIpLineVisibility(visible);
    }
    get isCenterLineVisible() {
        return this._cesiumRenderableAlignment.isCenterLineVisible;
    }
    setCenterLineVisibility(visible) {
        this._cesiumRenderableAlignment.setCenterLineVisibility(visible);
    }
    setSurfaceVisibility(show) {
        this._renderData.geometryPrimitives
            .filter(({ type }) => type === EmbankmentType.NONE)
            .forEach(({ primitive }) => {
            primitive.show = show;
        });
    }
    setEmbankmentVisibility(show) {
        this._renderData.geometryPrimitives
            .filter(({ type }) => type !== EmbankmentType.NONE)
            .forEach(({ primitive }) => {
            primitive.show = show;
        });
    }
    getGeometry() {
        return this._trenchGeometry.geometry;
    }
    fitToTrench() {
        const boundingSphere = this._cesiumRenderableAlignment.boundingSphere;
        this._viewer.camera.flyToBoundingSphere(boundingSphere, {
            offset: new HeadingPitchRange(CMath.toRadians(0.0), CMath.toRadians(-30.0), 0.0),
        });
    }
    _updateLongitudinalSlopeBreaks() {
        if (!this._alignment.ipLinePoints.length)
            return;
        const longitudinalSlope = this._trenchSettings.commonSetting.path.longitudinalSlope;
        const specifiedElevation = this._trenchSettings.commonSetting.path.specifiedElevation;
        const firstControlPoint = this._alignment.ipLinePoints[0];
        const lastControlPoint = this._alignment.ipLinePoints[this._alignment.ipLinePoints.length - 1];
        const firstControlPointPosition = firstControlPoint.localPoint;
        const lastControlPointPosition = lastControlPoint.localPoint;
        // Interpolates intermediate IP elevations locally from the two anchor points, since
        // Alignment.verticalAlignment isn't synced with this update until after this method returns.
        const getIntermediateIPCandidates = (startElevation, endElevation) => {
            const totalLength = this._alignment.lengthOnDatum;
            const ipLinePoints = this._alignment.ipLinePoints;
            return ipLinePoints
                .slice(1, ipLinePoints.length - 1)
                .map((controlPoint) => {
                const { distanceFromStart_2D } = this._alignment.getClosestPointInfoOnPath(controlPoint.localPoint);
                const elevation = totalLength === 0
                    ? startElevation
                    : startElevation +
                        (endElevation - startElevation) *
                            (distanceFromStart_2D / totalLength);
                return {
                    horizontalDistance: distanceFromStart_2D,
                    elevation,
                    associatedIPPointId: controlPoint.id,
                };
            });
        };
        const setStraightSlope = (endElevation) => {
            this._verticalAlignmentData = [
                {
                    horizontalDistance: 0,
                    elevation: specifiedElevation,
                    associatedIPPointId: firstControlPoint.id,
                },
                {
                    horizontalDistance: this._alignment.lengthOnDatum,
                    elevation: endElevation,
                    associatedIPPointId: lastControlPoint.id,
                },
                ...getIntermediateIPCandidates(specifiedElevation, endElevation),
            ].sort((a, b) => a.horizontalDistance - b.horizontalDistance);
        };
        switch (longitudinalSlope.slopeType) {
            case ELongitudinalSlopeType.DEFAULT:
                setStraightSlope(lastControlPointPosition.z -
                    (firstControlPointPosition.z - specifiedElevation));
                break;
            case ELongitudinalSlopeType.FLAT:
                setStraightSlope(specifiedElevation);
                break;
            case ELongitudinalSlopeType.CONSTANT: {
                const { slopeValue } = longitudinalSlope;
                const depthOfLastSlopeBreak = slopeValue !== 0 ? this._alignment.lengthOnDatum / slopeValue : 0;
                setStraightSlope(specifiedElevation + depthOfLastSlopeBreak);
                break;
            }
            case ELongitudinalSlopeType.NONE:
                const { depth } = longitudinalSlope;
                this._verticalAlignmentData = this._alignment.ipLinePoints.map((controlPoint) => {
                    const { distanceFromStart_2D } = this._alignment.getClosestPointInfoOnPath(controlPoint.localPoint);
                    return {
                        horizontalDistance: distanceFromStart_2D,
                        elevation: controlPoint.localPoint.z - depth,
                        associatedIPPointId: controlPoint.id,
                    };
                });
                break;
            default:
                break;
        }
        ActivityLogger.log(Activity.TRENCH_SLOPEBREAK_UPDATED);
    }
    getLinearData(trenchInfo) {
        try {
            const controlPointsLocal = this._alignment.ipLinePoints.map((p) => p.localPoint);
            const controlPointsArray = [];
            for (let i = 0; i < controlPointsLocal.length; i++) {
                let pointType;
                if (i === 0) {
                    pointType = PointType.BEGINNING_POINT;
                }
                else if (i === controlPointsLocal.length - 1) {
                    pointType = PointType.END_POINT;
                }
                else {
                    pointType = PointType.INTERMEDIATE_POINT;
                }
                const controlPoint = {
                    point: [
                        controlPointsLocal[i].x,
                        controlPointsLocal[i].y,
                        controlPointsLocal[i].z,
                    ],
                    pointType: pointType,
                };
                controlPointsArray.push(controlPoint);
            }
            const arbitraryLineJSON = {
                version: 2.0,
                lineId: this._id,
                controlPoints: controlPointsArray,
                associatedTerrain: {
                    id: this.associatedTerrain.id,
                    terrainType: this.associatedTerrain.assetType,
                },
            };
            const trenchLinearInfo = {
                version: 2.0,
                lineInfo: {
                    id: this._id,
                    label: trenchInfo.trenchName,
                    json: arbitraryLineJSON,
                    siteId: trenchInfo.siteID,
                    isVisible: trenchInfo.isVisible,
                },
            };
            return JSON.stringify(trenchLinearInfo);
        }
        catch (error) {
            //To do: implement mechanism to notify user.
            console.warn(error);
        }
    }
    rayCast(ray) {
        return EarthBrainUtils.intersectAgainstIGeometry(ray, this.getGeometry());
    }
    tracePoints(points) {
        return EarthBrainUtils.tracePointsOnGeometry(this._trenchGeometry.geometry, this.heightMap, points);
    }
    /**
     * Calculates and returns the axis-aligned bounding box (AABB) for the trench.
     * The AABB is computed based on the WGS84 coordinates of the control points and geometry vertices.
     * If no points are available, an empty AABB is returned.
     *
     * NOTE: using heighmap AABB or chaching AABB will enhance performance.
     *
     * @returns {AxisAlignedBoundingBox} The AABB of the trench.
     */
    trenchAABB_WGS84() {
        const geometryVerticesWGS84 = [];
        for (const geomData of this._wgs84dataMemo.geometries) {
            if (geomData.geometry.vertices.length > 0) {
                const unpackedVertices = Cartesian3.unpackArray(geomData.geometry.vertices);
                geometryVerticesWGS84.push(...unpackedVertices);
            }
        }
        if (geometryVerticesWGS84.length === 0) {
            return new AxisAlignedBoundingBox();
        }
        return AxisAlignedBoundingBox.fromPoints(geometryVerticesWGS84);
    }
    //-------------------------Private Methods-------------------------
    async recomputeGeometry() {
        const site = Site.getInstance();
        const intersectionData = getRaycastableFromRubbingDestination(site, this._associatedRubbingDestination);
        this._trenchGeometry.update(this._alignment, this._trenchSettings, intersectionData ? [intersectionData] : []);
        const trenchSegGeometries = this._trenchGeometry.getMergedGeometriesBySegmentSubType();
        // Create HeightMap
        this._heightMap = undefined;
        if (this._trenchGeometry.geometry.vertices.length > 0) {
            this._heightMap = HeightMap.FromIndexedGeometry(this._trenchGeometry.geometry, OBJECT_CELL_SIZE);
        }
        await this._updateWGS84Data(trenchSegGeometries);
    }
    //-----------private render helpers-----------
    async renderGeometry() {
        this.disposePrimitives();
        this._wgs84dataMemo.geometries.forEach((wgs84Geom) => {
            if (wgs84Geom.geometry.vertices.length > 0 &&
                wgs84Geom.geometry.vertices.length % 3 === 0) {
                const color = this._getColorBySegmentSide(wgs84Geom.side);
                const pickableType = this._getPickableTypeBySegmentSide(wgs84Geom.side);
                const primitive = EarthBrainUtils.createEntityFromIndexedGeometry(wgs84Geom.geometry, color, {
                    pickInfo: {
                        entity: { id: wgs84Geom.id },
                        assetType: AssetType.Trench_Data,
                        pickableType,
                        owner: this,
                        structureInfo: undefined, //{ id: this._id },
                    },
                });
                const wireframePrimitive = EarthBrainUtils.createEntityFromIndexedGeometry(wgs84Geom.geometry, Color.BLACK, {
                    pickInfo: {
                        entity: { id: wgs84Geom.id },
                        assetType: AssetType.Trench_Data,
                        pickableType,
                        owner: this,
                        structureInfo: undefined, //{ id: this._id },
                    },
                }, true);
                this._viewer.scene.primitives.add(primitive);
                this._viewer.scene.primitives.add(wireframePrimitive);
                this._renderData.geometryPrimitives.push({
                    primitive,
                    type: wgs84Geom.embankmentType,
                });
                this._renderData.geometryPrimitives.push({
                    primitive: wireframePrimitive,
                    type: wgs84Geom.embankmentType,
                });
            }
        });
    }
    disposePrimitives() {
        this._renderData.geometryPrimitives.forEach(({ primitive }) => {
            this._viewer.scene.primitives.remove(primitive);
            !primitive.isDestroyed() && primitive.destroy();
        });
        this._renderData.geometryPrimitives.length = 0;
    }
    disposeRenderData() {
        this._cesiumRenderableAlignment.dispose();
        this.disposePrimitives();
    }
    async render() {
        // Debug Entities
        this._removeDebugEntities();
        // Alignment
        await this._cesiumRenderableAlignment.render();
        // Geometry
        await this.renderGeometry();
        this._viewer.scene.requestRender();
    }
}
//# sourceMappingURL=earthbrain-trench.js.map